import { useEffect, useState } from 'react';
import axios from 'axios';
import RichTextArea from '@/components/RichTextArea';
import { API } from '@/App';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Mail, Send, Paperclip, AlertTriangle, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useEmailSendConfirmation } from '@/hooks/useEmailSendConfirmation';
import EventNoticeContentForm from './EventNoticeContentForm';
import { SenderNamePicker } from './ThankYouEmailPanel';

/**
 * Dialog for composing + sending Entry Pack emails. Each selected club
 * receives the Event Notice + Entry Summary Worksheet (+ Entry Forms for
 * Nationals/WorldSkate) as attachments.
 *
 * Props:
 *   open, onOpenChange
 *   competitionId — required once dialog opens
 *   competition — for the year / name label
 *   selectedClubs — list of {club_id, name, contact_email, missing_email}
 */
export default function EntryPackEmailDialog({
  open, onOpenChange, competitionId, competition, selectedClubs,
}) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [excludedAttachments, setExcludedAttachments] = useState([]);
  // Event Notice content settings (persisted on the competition record
  // so they drive both the standalone Event Notice dialog AND the
  // attachment generated here).
  const [enGreeting, setEnGreeting] = useState('Kia ora koutou');
  const [enIncludeFaqs, setEnIncludeFaqs] = useState(true);
  const [enLetterheadType, setEnLetterheadType] = useState('none');
  const [enSettingsSaved, setEnSettingsSaved] = useState(false);
  const [senderOptions, setSenderOptions] = useState({ default: '', options: [] });
  const { gate, ConfirmDialogs } = useEmailSendConfirmation();

  useEffect(() => {
    if (!open || !competitionId) return;
    setLoading(true);
    setExcludedAttachments([]);
    (async () => {
      try {
        const [s, a, en, snd] = await Promise.all([
          axios.get(`${API}/entry-pack-email/${competitionId}`,
            { withCredentials: true }),
          axios.get(`${API}/entry-pack-email/${competitionId}/available-attachments`,
            { withCredentials: true }),
          axios.get(`${API}/competitions/${competitionId}/event-notice-settings`,
            { withCredentials: true }),
          axios.get(`${API}/entry-pack-email/${competitionId}/sender-options`,
            { withCredentials: true }),
        ]);
        setSettings(s.data);
        setAttachments(a.data.attachments || []);
        setEnGreeting(en.data.greeting || 'Kia ora koutou');
        setEnIncludeFaqs(!!en.data.include_faqs);
        setEnLetterheadType(en.data.letterhead_type || 'none');
        setEnSettingsSaved(!!en.data.is_saved);
        setSenderOptions(snd.data || { default: '', options: [] });
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load email settings');
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, competitionId]);

  const update = (patch) => setSettings(prev => ({ ...(prev || {}), ...patch }));

  const persistSettings = async () => {
    if (!competitionId || !settings) return;
    try {
      await Promise.all([
        axios.put(
          `${API}/entry-pack-email/${competitionId}`,
          {
            greeting: settings.greeting || '',
            subject: settings.subject || '',
            body_html: settings.body_html || '',
            acknowledgement: settings.acknowledgement || '',
            sender_key: settings.sender_key || null,
          },
          { withCredentials: true }
        ),
        axios.put(
          `${API}/competitions/${competitionId}/event-notice-settings`,
          { greeting: enGreeting, include_faqs: enIncludeFaqs },
          { withCredentials: true }
        ),
      ]);
      setEnSettingsSaved(true);
    } catch (err) {
      console.warn('Could not persist Entry Pack email settings:', err);
    }
  };

  const runSend = async (live) => {
    setSending(true);
    try {
      await persistSettings();
      const club_ids = (selectedClubs || []).filter(c => !c.missing_email).map(c => c.club_id);
      if (!club_ids.length) {
        toast.warning('No selected clubs have an email address on file');
        return;
      }
      const res = await axios.post(
        `${API}/entry-pack-email/${competitionId}/send`,
        { club_ids, live, exclude_attachments: excludedAttachments },
        { withCredentials: true, timeout: 180000 }
      );
      const sentCount = (res.data.sent || []).length;
      const skippedCount = (res.data.skipped || []).length;
      if (sentCount) toast.success(`${sentCount} email${sentCount === 1 ? '' : 's'} ${live ? 'sent' : 'sent (dev mode → failsafe)'}`);
      if (skippedCount) toast.warning(`${skippedCount} skipped — see console for reasons`);
      if (skippedCount) console.warn('Skipped:', res.data.skipped);
    } catch (err) {
      const status = err.response?.status;
      let msg;
      if (status === 502 || status === 504 || err.code === 'ECONNABORTED') {
        msg = 'Server is still processing the send — check your inbox in a minute or refresh and look at the email log.';
      } else {
        msg = err.response?.data?.detail || err.message || 'Send failed';
      }
      toast.error(msg);
      console.error('Entry pack send failed:', err);
    } finally {
      setSending(false);
      onOpenChange(false);
    }
  };

  const handleSendClick = () => {
    gate((live) => { runSend(live); });
  };

  const deliverable = (selectedClubs || []).filter(c => !c.missing_email);
  const missing = (selectedClubs || []).filter(c => c.missing_email);
  const hasAttachments = attachments.length === 0
    || attachments.some(a => !excludedAttachments.includes(a.key));

  return (
    <>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="entry-pack-email-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#5C4033]" />
              Email Entry Packs
            </AlertDialogTitle>
            <AlertDialogDescription>
              Compose the covering email. Each club receives their own Entry Pack as one or more attachment(s).
            </AlertDialogDescription>
          </AlertDialogHeader>

          {loading && <p className="text-sm text-stone-600">Loading…</p>}

          {!loading && settings && (
            <div className="space-y-5 mt-2">
              <div>
                <Label>Email Subject</Label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={settings.subject || ''}
                  onChange={(e) => update({ subject: e.target.value })}
                  data-testid="ep-email-subject-input"
                />
              </div>

              <div className="rounded-lg border bg-amber-50/60 border-amber-200 px-3 py-2 text-sm text-amber-900">
                <p>
                  <span className="font-semibold">{deliverable.length}</span> club{deliverable.length === 1 ? '' : 's'} will receive an email.
                  {missing.length > 0 && (
                    <> <AlertTriangle className="inline h-4 w-4 text-amber-700 mb-0.5" />{' '}
                      <span className="font-semibold">{missing.length}</span> skipped (no email on file):{' '}
                      {missing.map(c => c.name).join(', ')}
                    </>
                  )}
                </p>
              </div>

              <div>
                <Label>Greeting</Label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={settings.greeting || ''}
                  onChange={(e) => update({ greeting: e.target.value })}
                  data-testid="ep-email-greeting-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Body text of the email</Label>
                  <button
                    type="button"
                    className="text-xs text-stone-600 underline hover:text-stone-800"
                    onClick={async () => {
                      try {
                        const res = await axios.get(
                          `${API}/entry-pack-email/${competitionId}/default-body`,
                          { withCredentials: true }
                        );
                        update({ body_html: res.data.body_html });
                        toast.success('Body reset to default');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to load default text');
                      }
                    }}
                    data-testid="ep-reset-body-btn"
                  >
                    Reset Default Text
                  </button>
                </div>
                <p className="text-xs text-stone-500 mb-1">
                  Variables are pre-filled with actual values from the competition record.
                </p>
                <RichTextArea
                  value={settings.body_html || ''}
                  onChange={(html) => update({ body_html: html })}
                  testId="ep-email-body-editor"
                />
              </div>

              <div>
                <Label>Acknowledgement</Label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={settings.acknowledgement || ''}
                  onChange={(e) => update({ acknowledgement: e.target.value })}
                  data-testid="ep-email-acknowledgement-input"
                />
              </div>

              <SenderNamePicker
                options={senderOptions}
                value={settings.sender_key}
                onChange={(key) => update({ sender_key: key })}
                testIdPrefix="ep-email"
              />

              <div className="rounded-lg border bg-stone-50 px-3 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <Paperclip className="h-4 w-4 text-stone-500" />
                  <span className="font-semibold text-sm">Attachments</span>
                </div>
                {(() => {
                  const sortedAttachments = [...attachments].sort((a, b) => {
                    // Event Notice last so it sits right above the Event
                    // Notice content form that follows.
                    if (a.key === 'event_notice') return 1;
                    if (b.key === 'event_notice') return -1;
                    return 0;
                  });
                  const included = sortedAttachments.filter(a => !excludedAttachments.includes(a.key));
                  const eventNoticeIncluded = included.some(a => a.key === 'event_notice');
                  if (!sortedAttachments.length) {
                    return <p className="text-xs text-stone-600">No attachments resolved.</p>;
                  }
                  return (
                    <>
                      <ul className="divide-y" data-testid="ep-attachments-list">
                        {sortedAttachments.map(att => {
                          const isExcluded = excludedAttachments.includes(att.key);
                          const canDelete = included.length > 1 && !isExcluded;
                          const canRestore = isExcluded;
                          return (
                            <li
                              key={att.key}
                              className={`flex items-center justify-between py-1.5 ${isExcluded ? 'opacity-50' : ''}`}
                            >
                              <div className="text-sm">
                                <span className={`font-medium ${isExcluded ? 'line-through' : ''}`}>
                                  {att.description}
                                </span>
                                <span className="text-xs text-stone-600 ml-2">{att.filename}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {canRestore && (
                                  <button
                                    type="button"
                                    className="text-xs text-stone-600 underline hover:text-stone-800"
                                    onClick={() => setExcludedAttachments(prev => prev.filter(k => k !== att.key))}
                                    data-testid={`ep-attachment-restore-${att.key}`}
                                  >
                                    Restore
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    type="button"
                                    aria-label={`Remove ${att.description}`}
                                    className="p-1 rounded hover:bg-stone-200 text-stone-600"
                                    onClick={() => setExcludedAttachments(prev => [...prev, att.key])}
                                    data-testid={`ep-attachment-remove-${att.key}`}
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                      <p className="text-xs text-stone-600 mt-2">
                        Remove attachments to send a corrected file to the same recipients without re-sending everything else. The button appears only when more than one attachment is present.
                      </p>

                      {eventNoticeIncluded && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex items-center gap-2 mb-3">
                            <FileText className="h-4 w-4 text-[#2D4A3E]" />
                            <span className="font-semibold text-sm text-[#2D4A3E]">Event Notice content</span>
                            {!enSettingsSaved && (
                              <span
                                className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200"
                                data-testid="ep-en-defaults-badge"
                              >
                                Using defaults
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-stone-600 mb-3">
                            These settings shape the attached Event Notice document. They're saved per-competition and shared with the standalone &ldquo;Generate Event Notice&rdquo; button, so you only need to set them once.
                          </p>
                          <EventNoticeContentForm
                            competitionId={competitionId}
                            greeting={enGreeting}
                            onGreetingChange={setEnGreeting}
                            includeFaqs={enIncludeFaqs}
                            onIncludeFaqsChange={setEnIncludeFaqs}
                            letterheadType={enLetterheadType}
                            onLetterheadTypeChange={setEnLetterheadType}
                            testIdPrefix="ep-en"
                          />
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ep-email-close-btn">Close</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleSendClick(); }}
              disabled={sending || loading || deliverable.length === 0 || !hasAttachments}
              className="bg-[#5C4033] hover:bg-[#3d2a22] text-white"
              data-testid="ep-email-send-btn"
            >
              <Send className="mr-2 h-4 w-4" />
              {sending ? 'Sending…' : 'Send Emails'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmDialogs />
    </>
  );
}
